import { useState } from "react";
import { ShoppingBag } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Product } from "@nh/shared";
import { Button } from "@/shared/ui/core";
import { maxQuantityFor, QuantityStepper, useCart } from "@/features/cart";
import { PackageUpsellDialog } from "./PackageUpsellDialog";

export interface ProductPurchaseProps {
  product: Product;
  /** El nombre YA resuelto en el idioma activo — entra en los rótulos. */
  name: string;
}

/**
 * Elegir cuántos y meterlos al carrito.
 *
 * ── La cantidad es estado LOCAL hasta que se pulsa "Añadir" ──────────
 * Escribir "3" en el selector no debe cambiar el carrito: quien está
 * decidiendo todavía no ha decidido. El carrito se toca una sola vez, con
 * el botón, y así "deshacer" es no pulsarlo.
 *
 * ── Después de añadir, el selector vuelve a 1 ────────────────────────
 * Si se quedara en 3, el siguiente "Añadir" metería otros tres sin que
 * nadie lo pidiera — y el carrito ya suma la cantidad de la línea que ya
 * estaba, así que ese tres sería un seis.
 *
 * ── Y el panel se abre ───────────────────────────────────────────────
 * Es la confirmación. Sin ella, la única señal de que algo pasó es un
 * número diminuto que cambia en la esquina de la pantalla, y la reacción
 * normal es pulsar otra vez. El panel enseña lo que se acaba de añadir,
 * con su precio y su subtotal, y se cierra con Escape.
 *
 * ── Agotado apaga los dos controles ──────────────────────────────────
 * Y no los esconde: un botón que desaparece deja a la persona buscando
 * dónde estaba. `maxQuantityFor` devuelve 0 cuando el catálogo declara
 * cero existencias — el producto sin cuenta llevada (`stock` ausente) NO
 * está agotado, solo no se sabe, y ahí se puede comprar.
 */
export function ProductPurchase({ product, name }: ProductPurchaseProps) {
  const { t } = useTranslation();
  const { add, open } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [upsell, setUpsell] = useState(false);

  const max = maxQuantityFor(product.stock);
  const soldOut = max === 0;

  /*
   * ── Añadir ya no abre el carrito: abre el CTA de paquetes ──────────
   * Es el journey de Anahí (26-ago): tras "agregar al carrito", el
   * llamado va hacia adquirir un paquete (hasta 15 % de ahorro). El
   * producto SÍ quedó añadido — el CTA es una bifurcación, no una
   * condición. "Comprar solo este pedido" abre el carrito de siempre.
   */
  const addToCart = () => {
    add(product, quantity);
    setQuantity(1);
    setUpsell(true);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <QuantityStepper
        value={quantity}
        onChange={setQuantity}
        // Con el producto agotado el selector se apaga igual; el `1` evita
        // un tope de 0, que dejaría el campo en un estado imposible.
        max={soldOut ? 1 : max}
        label={name}
        disabled={soldOut}
      />

      <Button
        variant="primary"
        size="lg"
        leftIcon={<ShoppingBag />}
        disabled={soldOut}
        onClick={addToCart}
        className="flex-1 sm:flex-none"
      >
        {soldOut ? t("Agotado") : t("Añadir al carrito")}
      </Button>

      {upsell && (
        <PackageUpsellDialog
          onClose={() => {
            setUpsell(false);
            open();
          }}
          onOnlyThisOrder={() => {
            setUpsell(false);
            open();
          }}
        />
      )}
    </div>
  );
}
